<template>
  <div class="min-h-screen bg-gray-100">
    <div class="no-print sticky top-0 z-10 flex items-center justify-between bg-gray-900 px-6 py-3 text-white">
      <router-link to="/admin/ledger" class="text-sm font-semibold text-gray-300 hover:text-white">&larr; Back to Companies</router-link>
      <div v-if="company" class="flex items-center gap-2">
        <span v-if="shareFeedback" class="text-xs font-medium text-gray-300">{{ shareFeedback }}</span>
        <button
          @click="share"
          :disabled="sharing"
          class="rounded-full border border-gray-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-60"
        >
          {{ sharing ? 'Preparing...' : 'Share' }}
        </button>
        <button
          @click="print"
          class="rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-600"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-20 text-center text-gray-400">Loading...</div>
    <div v-else-if="!company" class="py-20 text-center text-gray-500">Company not found.</div>

    <div v-else ref="doc" class="mx-auto my-8 max-w-5xl overflow-hidden rounded-[28px] bg-white shadow-lg print:my-0 print:max-w-none print:rounded-none print:shadow-none">
      <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>

      <div class="p-10">
        <!-- Letterhead -->
        <div class="flex flex-col items-center border-b border-amber-100 pb-6 text-center">
          <img :src="etimadLogo" alt="Al Etimad Haramain Travel" class="h-14 w-auto" />
          <p class="mt-2 text-xs text-gray-500">aletimadtravel@gmail.com &middot; +92 318 5073318 &middot; aletimadtravel.com</p>
        </div>

        <h1 class="mt-7 text-center text-2xl font-bold text-gray-900">Customer Ledger</h1>

        <!-- Balance summary (top, not part of the rows table) -->
        <div class="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div>
            <p><span class="font-semibold text-gray-700">Customer / Company Name:</span> {{ company.name }}</p>
            <p><span class="font-semibold text-gray-700">ETD-ID:</span> {{ company.etdId }}</p>
            <p v-if="company.registrationNumber"><span class="font-semibold text-gray-700">Registration Number:</span> {{ company.registrationNumber }}</p>
          </div>
          <p class="text-base">
            <span class="font-semibold text-gray-700">Amount Receivable:</span>
            <span class="font-bold text-amber-600">{{ money(runningBalance) }}</span>
          </p>
        </div>

        <!-- table-fixed with explicit column widths so the table always fits
             the page width when printed to real paper. On a narrow phone
             screen those same percentage widths would squeeze each column
             to only a few pixels, so the columns other than Description
             (which wraps) would overflow their cell and visually overlap
             the next column -- min-w-[720px] + overflow-x-auto keeps every
             column legible (scrollable on screen, and captured at its full
             width -- not the narrow viewport -- when rendered to PDF for
             sharing), while the print: rules below still shrink it back to
             fit the page for actual printing. Description is the last
             column, sized generously and set to wrap onto multiple lines
             (never white-space:nowrap) so long text grows the row taller
             instead of getting clipped. -->
        <div class="mt-6 overflow-x-auto rounded-2xl border border-gray-100">
          <table class="w-full min-w-[720px] table-fixed text-sm print:min-w-0 print:text-[11px]">
            <colgroup>
              <col class="w-[4%]" />
              <col class="w-[8%]" />
              <col class="w-[12%]" />
              <col class="w-[7%]" />
              <col class="w-[9%]" />
              <col class="w-[9%]" />
              <col class="w-[9%]" />
              <col class="w-[42%]" />
            </colgroup>
            <thead>
              <tr class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th class="px-3 py-2 font-semibold print:px-2 print:py-1.5">Sr#</th>
                <th class="px-3 py-2 font-semibold print:px-2 print:py-1.5">Date</th>
                <th class="px-3 py-2 font-semibold print:px-2 print:py-1.5">Type</th>
                <th class="px-3 py-2 font-semibold print:px-2 print:py-1.5">Ref#</th>
                <th class="px-3 py-2 font-semibold text-right print:px-2 print:py-1.5">Debit ({{ currency }})</th>
                <th class="px-3 py-2 font-semibold text-right print:px-2 print:py-1.5">Credit ({{ currency }})</th>
                <th class="px-3 py-2 font-semibold text-right print:px-2 print:py-1.5">Balance ({{ currency }})</th>
                <th class="px-3 py-2 font-semibold print:px-2 print:py-1.5">Description</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, i) in rows" :key="i">
                <td class="px-3 py-2.5 align-top text-gray-700 print:px-2 print:py-1.5">{{ i + 1 }}</td>
                <td class="px-3 py-2.5 align-top text-gray-700 print:px-2 print:py-1.5">{{ row.date }}</td>
                <td class="px-3 py-2.5 align-top text-gray-700 print:px-2 print:py-1.5">{{ row.type }}</td>
                <td class="px-3 py-2.5 align-top text-gray-700 print:px-2 print:py-1.5">{{ row.refNo }}</td>
                <td class="px-3 py-2.5 align-top text-right text-gray-700 print:px-2 print:py-1.5">{{ formatNumber(row.debit) }}</td>
                <td class="px-3 py-2.5 align-top text-right text-gray-700 print:px-2 print:py-1.5">{{ formatNumber(row.credit) }}</td>
                <td class="px-3 py-2.5 align-top text-right font-semibold text-gray-900 print:px-2 print:py-1.5">{{ formatNumber(row.balance) }}</td>
                <td class="whitespace-normal break-words px-3 py-2.5 align-top text-gray-600 print:px-2 print:py-1.5">{{ row.description }}</td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="8" class="px-3 py-8 text-center text-gray-400">No ledger entries yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="h-2 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>
    </div>
  </div>
</template>

<script>
import { getItem, listItems } from '../../services/dataStore'
import { formatAmount } from '../../services/currencyStore'
import { printDocument } from '../../utils/printHelper'
import { shareLinkWithPdf } from '../../utils/shareHelper'
import { generatePdfBlob } from '../../utils/pdfHelper'
import etimadLogo from '../../assets/images/etimad-logo-wide.png'

export default {
  name: 'LedgerPrint',
  data() {
    return {
      loading: true,
      company: null,
      ledger: null,
      etimadLogo,
      shareFeedback: '',
      sharing: false
    }
  },
  computed: {
    currency() {
      return this.ledger?.currency || 'PKR'
    },
    rows() {
      const entries = this.ledger?.rows || []
      const sorted = [...entries].sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      let balance = 0
      return sorted.map(entry => {
        balance += (Number(entry.debit) || 0) - (Number(entry.credit) || 0)
        return { ...entry, balance }
      })
    },
    runningBalance() {
      const rows = this.rows
      return rows.length ? rows[rows.length - 1].balance : 0
    }
  },
  async mounted() {
    const companyId = this.$route.params.companyId
    this.company = await getItem('companies', companyId)
    if (this.company) {
      const ledgers = await listItems('ledgers', [])
      this.ledger = ledgers.find(l => l.companyId === companyId) || null
    }
    this.loading = false
  },
  methods: {
    formatNumber(n) {
      return (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    money(amount) {
      return formatAmount(amount, this.currency)
    },
    print() {
      printDocument()
    },
    async share() {
      this.sharing = true
      let pdfBlob = null
      try {
        if (this.$refs.doc) pdfBlob = await generatePdfBlob(this.$refs.doc)
      } catch (e) {
        pdfBlob = null
      }
      const result = await shareLinkWithPdf(
        window.location.href,
        `${this.company.name} - Customer Ledger`,
        pdfBlob,
        `${this.company.etdId}-ledger.pdf`
      )
      if (result.downloaded) {
        this.shareFeedback = result.copied ? 'PDF downloaded, link copied!' : 'PDF downloaded!'
        setTimeout(() => { this.shareFeedback = '' }, 3500)
      } else if (result.copied) {
        this.shareFeedback = 'Link copied!'
        setTimeout(() => { this.shareFeedback = '' }, 2500)
      }
      this.sharing = false
    }
  }
}
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
